/**
 * @module M/impl/control/FullTOCControl
 */
export default class FullTOCControl extends M.impl.Control {
  /**
   * This function adds the control to the specified map
   *
   * @public
   * @function
   * @param {M.Map} map to add the plugin
   * @param {HTMLElement} html of the plugin
   * @api stable
   */
  addTo(map, html) {
    this.facadeMap_ = map;
    this.facadeControl_ = undefined;
    super.addTo(map, html);
  }

  /**
   * Registers events on map and layers to render the
   * fulltoc
   *
   * @public
   * @function
   * @api stable
   */
  registerEvents() {
    if (!M.utils.isNullOrEmpty(this.facadeMap_)) {
      const olMap = this.facadeMap_.getMapImpl();
      this.registerViewEvents_(olMap.getView());
      this.registerLayersEvents_(olMap.getLayers());
      olMap.on('change:view', () => this.onViewChange_.bind(this));
    }
  }

  /**
   * Unegisters events for map and layers from the fulltoc
   *
   * @public
   * @function
   * @api stable
   */
  unregisterEvents() {
    if (!M.utils.isNullOrEmpty(this.facadeMap_)) {
      const olMap = this.facadeMap_.getMapImpl();
      this.unregisterViewEvents_(olMap.getView());
      this.unregisterLayersEvents_(olMap.getLayers());
      olMap.un('change:view', () => this.onViewChange_.bind(this));
    }
  }

  /**
   *
   */
  registerViewEvents_(view) {
    view.on('change:resolution', () => this.facadeControl_.render());
  }

  /**
   *
   */
  registerLayersEvents_(layers) {
    layers.forEach(this.registerLayerEvents_.bind(this));
    layers.on('remove', () => this.facadeControl_.render());
    layers.on('add', () => this.onAddLayer_.bind(this));
  }

  /**
   *
   */
  registerLayerEvents_(layer) {
    layer.on('change:visible', () => this.facadeControl_.render());
    layer.on('change:extent', () => this.facadeControl_.render());
  }

  /**
   *
   */
  unregisterViewEvents_(view) {
    view.un('change:resolution', () => this.facadeControl_.render());
  }

  /**
   *
   */
  unregisterLayersEvents_(layers) {
    layers.forEach(this.unregisterLayerEvents_.bind(this));
    layers.un('remove', () => this.facadeControl_.render());
    layers.un('add', () => this.onAddLayer_.bind(this));
  }

  /**
   *
   */
  unregisterLayerEvents_(layer) {
    layer.un('change:visible', () => this.facadeControl_.render());
    layer.un('change:extent', () => this.facadeControl_.render());
  }

  /**
   *
   */
  onViewChange_(evt) {
    // removes listener from previous view
    this.unregisterViewEvents_(evt.oldValue);

    // attaches listeners to the new view
    const olMap = this.facadeMap_.getMapImpl();
    this.registerViewEvents_(olMap.getView());
  }

  /**
   *
   */
  onAddLayer_(evt) {
    this.registerLayerEvents_(evt.element);
    this.facadeControl_.render();
  }

  /**
   *
   */
  getGeoJSONExtent(layer) {
    return layer.getImpl().getOL3Layer().getSource().getExtent();
  }

  /**
   * Loads GeoJSON layer
   * @public
   * @function
   * @param {*} source-
   */
  loadAllInGeoJSONLayer(sources, layerName) {
    let features = [];
    sources.forEach((source) => {
      const localFeatures = new ol.format.GeoJSON()
        .readFeatures(source, { featureProjection: this.facadeMap_.getProjection().code });
      if (localFeatures !== null && localFeatures !== undefined && localFeatures.length > 0) {
        features = features.concat(localFeatures);
      }
    });

    features = this.featuresToFacade(features);
    const layer = new M.layer.Vector({ name: layerName, legend: layerName, extract: false });
    layer.addFeatures(features);
    this.facadeMap_.addLayers(layer);
    layer.setZIndex(layer.getZIndex() + 8);
    return features;
  }

  /**
   * Loads KML layer
   * @public
   * @function
   * @api
   * @param {*} source -
   * @param {*} extractStyles -
   */
  loadKMLLayer(source, layerName, extractStyles) {
    let features = new ol.format.KML({ extractStyles })
      .readFeatures(source, { featureProjection: this.facadeMap_.getProjection().code });

    features = this.featuresToFacade(features);
    features = this.geometryCollectionParse(features);
    const others = [];
    const lines = [];
    features.forEach((f) => {
      if (f.getGeometry().type.toLowerCase().indexOf('linestring') > -1) {
        lines.push(f);
      } else {
        others.push(f);
      }
    });

    if (lines.length > 0) {
      const layer = new M.layer.Vector({ name: `${layerName}_lines`, legend: `${layerName}_lines`, extract: false });
      layer.addFeatures(lines);
      this.facadeMap_.addLayers(layer);
      layer.setZIndex(layer.getZIndex() + 8);
    }

    if (others.length > 0) {
      const layer = new M.layer.Vector({ name: layerName, legend: layerName, extract: false });
      layer.addFeatures(others);
      this.facadeMap_.addLayers(layer);
      layer.setZIndex(layer.getZIndex() + 8);
    }

    return features;
  }

  /**
   * Loads GPX layer.
   * @public
   * @function
   * @api
   * @param {*} source -
   */
  loadGPXLayer(source, layerName) {
    let features = [];
    const origFeatures = new ol.format.GPX()
      .readFeatures(source, { featureProjection: this.facadeMap_.getProjection().code });
    origFeatures.forEach((f) => {
      if (f.getGeometry().getType() === 'MultiLineString') {
        if (f.getGeometry().getLineStrings().length === 1) {
          const geom = f.getGeometry().getLineStrings()[0];
          f.setGeometry(geom);
        }
      }

      features.push(f);
    });

    let lines = features.filter((f) => {
      return f.getGeometry().getType().indexOf('LineString') > -1;
    });

    let points = features.filter((f) => {
      return f.getGeometry().getType().indexOf('Point') > -1;
    });

    lines = this.featuresToFacade(lines);
    const layer = new M.layer.Vector({ name: layerName, legend: layerName, extract: false });
    layer.addFeatures(lines);
    this.facadeMap_.addLayers(layer);
    layer.setZIndex(layer.getZIndex() + 8);

    if (points.length > 0) {
      points = this.featuresToFacade(points);
      const layer2 = new M.layer.Vector({ name: `${layerName}_points`, legend: `${layerName}_points`, extract: false });
      layer2.addFeatures(points);
      this.facadeMap_.addLayers(layer2);
      layer2.setZIndex(layer2.getZIndex() + 8);
      features = lines.concat(points);
    } else {
      features = lines;
    }

    return features;
  }

  /**
   * Loads GML layer
   * @public
   * @function
   * @param {*} source -
   */
  loadGMLLayer(source, layerName) {
    let newSource = source;
    let srs = this.facadeMap_.getProjection().code;
    if (newSource.indexOf('srsName="GCS_WGS_1984"') > -1) {
      newSource = newSource.replace(/srsName="GCS_WGS_1984"/gi, 'srsName="EPSG:4326"');
    }

    if (newSource.indexOf('cp:geometry') > -1) {
      newSource = newSource.replace(/cp:geometry/gi, 'ogr:geometryProperty');
    }

    if (newSource.indexOf('certificacion:the_geom') > -1) {
      newSource = newSource.replace(/certificacion:the_geom/gi, 'ogr:geometryProperty');
    }

    if (newSource.split('srsName="')[1].split('"')[0].indexOf('http') > -1) {
      try {
        srs = `EPSG:${newSource.split('srsName="')[1].split('#')[1].split('"')[0]}`;
      } catch (err) {
        srs = `EPSG:${newSource.split('srsName="')[1].split('/EPSG/')[1].split('/')[1]}`;
      }
    } else if (newSource.split('srsName="')[1].indexOf('crs:EPSG::') > -1) {
      srs = `EPSG:${newSource.split('srsName="')[1].split('::')[1].split('"')[0]}`;
    } else {
      srs = newSource.split('srsName="')[1].split('"')[0];
    }

    if (newSource.indexOf('<member>') > -1) {
      newSource = newSource.replace(/member/gi, 'gml:featureMember');
    } else if (newSource.indexOf('<wfs:member>') > -1) {
      newSource = newSource.replace(/wfs:member/gi, 'gml:featureMember');
    } else if (newSource.indexOf('<ogr:featureMember>') > -1) {
      newSource = newSource.replace(/ogr:featureMember/gi, 'gml:featureMember');
    }

    let features = new ol.format.WFS({ gmlFormat: new ol.format.GML2() }).readFeatures(newSource, {
      dataProjection: srs,
      featureProjection: this.facadeMap_.getProjection().code,
    });

    features = features.map((f, index) => {
      const newF = f;
      if (!f.getGeometry()) {
        newF.setGeometry(f.get('geometry'));
      }

      return newF;
    });

    if (features.length === 0 || features[0].getGeometry() === undefined) {
      features = new ol.format.WFS({ gmlFormat: new ol.format.GML3() }).readFeatures(newSource, {
        dataProjection: srs,
        featureProjection: this.facadeMap_.getProjection().code,
      });

      features = features.map((f, index) => {
        const newF = f;
        if (!f.getGeometry()) {
          newF.setGeometry(f.get('geometry'));
        }
        return newF;
      });
    }

    // En el caso de que no tenga geometrías, comprobamos si es GML 3.2,
    // si lo es tenemos que parsearlo a mano.
    if ((features.length === 0 || features[0].getGeometry() === undefined) &&
      newSource.indexOf('gml/3.2') > 0) {
      features = this.gmlParser(newSource);
    }

    features = this.featuresToFacade(features);
    const layer = new M.layer.Vector({ name: layerName, legend: layerName, extract: false });
    layer.addFeatures(features);
    this.facadeMap_.addLayers(layer);
    layer.setZIndex(layer.getZIndex() + 8);
    return features;
  }

  /**
   * Loads GeoJSON layer
   * @public
   * @function
   * @param {*} source2 -
   */
  loadGeoJSONLayer(source, layerName) {
    let features = new ol.format.GeoJSON()
      .readFeatures(source, { featureProjection: this.facadeMap_.getProjection().code });

    features = this.featuresToFacade(features);
    features = features.filter((f) => {
      return f.getGeometry() !== null;
    });

    const layer = new M.layer.Vector({ name: layerName, legend: layerName, extract: false });
    layer.addFeatures(features);
    this.facadeMap_.addLayers(layer);
    layer.setZIndex(layer.getZIndex() + 8);
    return features;
  }

  /**
   * Converts Openlayers features to Mapea features.
   * @public
   * @function
   * @api
   * @param {Array<OL.Feature>} implFeatures
   * @returns {Array<M.Feature>}
   */
  featuresToFacade(implFeatures) {
    return implFeatures.map((feature) => {
      return M.impl.Feature.olFeature2Facade(feature);
    });
  }

  /**
   * Turns GeometryCollection features into single geometry features.
   * @public
   * @function
   * @api
   * @param {Array<M.Feature>} features
   */
  geometryCollectionParse(features) {
    const parsedFeatures = [];
    features.forEach((feature) => {
      if (feature.getGeometry().type === 'GeometryCollection') {
        const geometries = feature.getGeometry().geometries;
        geometries.forEach((geometry) => {
          const num = Math.random();
          const newFeature = new M.Feature(`mf${num}`, {
            type: 'Feature',
            id: `gf${num}`,
            geometry: {
              type: geometry.type,
              coordinates: geometry.coordinates,
            },
          });
          parsedFeatures.push(newFeature);
        });
      } else {
        parsedFeatures.push(feature);
      }
    });
    return parsedFeatures;
  }

  /**
   * Centers on features
   * @public
   * @function
   * @api
   * @param {*} features -
   */
  centerFeatures(features, isGPX) {
    if (!M.utils.isNullOrEmpty(features)) {
      if ((features.length === 1) && (features[0].getGeometry().type === 'Point')) {
        const pointView = new ol.View({
          center: features[0].getGeometry().coordinates,
          zoom: 15,
        });
        this.facadeMap_.getMapImpl().setView(pointView);
      } else {
        const extent = M.impl.utils.getFeaturesExtent(features);
        this.facadeMap_.getMapImpl().getView().fit(extent, {
          duration: 500,
          minResolution: 1,
        });
      }

      features.forEach((f) => {
        switch (f.getGeometry().type) {
          case 'Point':
          case 'MultiPoint':
            const newPointStyle = {
              radius: 6,
              fill: {
                color: '#71a7d3',
              },
              stroke: {
                color: 'white',
                width: 2,
              },
            };

            if (isGPX && f.getAttributes().name !== undefined && f.getAttributes().name !== '') {
              newPointStyle.label = {
                fill: {
                  color: '#ff0000',
                },
                stroke: {
                  color: 'white',
                  width: 2,
                  linedash: [0, 0],
                  linedashoffset: 0,
                  linecap: 'none',
                  linejoin: 'none',
                },
                scale: 2,
                text: f.getAttributes().name,
                font: '8px sanserif',
                align: 'center',
                baseline: 'top',
                rotate: false,
                rotation: 0,
                offset: [0, 10],
              };
            }

            if (f !== undefined) f.setStyle(new M.style.Point(newPointStyle));
            break;
          case 'LineString':
          case 'MultiLineString':
            const newLineStyle = new M.style.Line({
              stroke: {
                color: '#71a7d3',
                width: 6,
                linedash: undefined,
              },
            });
            if (f !== undefined) f.setStyle(newLineStyle);
            break;
          case 'Polygon':
          case 'MultiPolygon':
            const newPolygonStyle = new M.style.Polygon({
              fill: {
                color: '#71a7d3',
                opacity: 0.2,
              },
              stroke: {
                color: '#71a7d3',
                width: 6,
              },
            });
            if (f !== undefined) f.setStyle(newPolygonStyle);
            break;
          default:
            break;
        }
      });
    }
  }
}
